import React, { useEffect, useMemo } from 'react';

import DetachedDropdown, {
  detachedDropdownFallbackValue,
  DropdownValue,
} from '@/components/form/dropdown/DetachedDropdown';
import {
  GIG_GAMES_CATEGORIES_RECORD,
  useGameCategoryNavActions,
  useGameCategoryNavData,
} from '@/store/mexico/gamesCategoryNavStore';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import { useSelector } from 'react-redux';
import { AbsoluteNavLink } from '@/components/AbsoluteNavLink';
import { HomeIcon } from '@heroicons/react/24/outline';
import { IntlShape, useIntl } from 'react-intl';
import classNames from 'classnames';
import { useLocation } from 'react-use';
import { selectIsAuthenticated } from '@/store/selectors/authSelectors';
import { envIsMexico, envIsNormal } from '@/constants/constants';
import { GAMES_FEATURES_MEXICO } from '@/constants/constants-mexico';
import { GAMES_FEATURES_THAILAND } from '@/constants/constants-thailand';
import { selectIsThailand } from '@/store/selectors/commonSelectors';
import CategorySelector from '@/components/shared/games-category-nav/CategorySelector';
import { BffGamesCategory } from '@lucky7ventures/bff-types';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import { selectGameProvidersList } from '@/store/selectors/gamesSelectors';

const initialProviderOption = (intl: IntlShape) => ({
  label: intl.formatMessage({ id: 'games.nav.providers' }),
  value: detachedDropdownFallbackValue,
});

const initialFeatureOption = (intl: IntlShape) => ({
  label: intl.formatMessage({ id: 'games.nav.features' }),
  value: detachedDropdownFallbackValue,
});

enum NavSections {
  Categories,
  Providers,
  Features,
}

function GamesCategoryNav(): JSX.Element {
  const { feature, provider, category } = useGameCategoryNavData();
  const { setFeature, setProvider, setCategory, reset } = useGameCategoryNavActions();
  const navigate = useAbsoluteNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const intl = useIntl();
  const isThailand = useSelector(selectIsThailand);
  const gameProvidersList = useSelector(selectGameProvidersList);

  const getProviderName = (slug: string): string => {
    return gameProvidersList.find(i => i.slug === slug)?.name || '';
  };

  // dynamic ordering of the main sections of the navbar
  const sectionOrder = useMemo(() => {
    if (envIsMexico()) {
      return [NavSections.Categories, NavSections.Features, NavSections.Providers];
    }

    if (envIsNormal() && isThailand) {
      return [NavSections.Providers, NavSections.Categories, NavSections.Features];
    }

    return [NavSections.Categories];
  }, [isThailand]);

  const providersDropdownOptions: DropdownValue[] = useMemo(() => {
    if (!gameProvidersList || gameProvidersList.length === 0) {
      return [];
    }
    return gameProvidersList.reduce((acc: DropdownValue[], item) => {
      acc.push({
        label: item.name,
        value: item.slug,
      });
      return acc;
    }, []);
  }, [gameProvidersList]);

  // List of features for a dropdown
  // if empty the Features dropdown won't be shown
  const featuresList = useMemo(() => {
    if (envIsMexico()) {
      return [...GAMES_FEATURES_MEXICO];
    }

    if (envIsNormal() && isThailand) {
      return [...GAMES_FEATURES_THAILAND];
    }

    return [];
  }, [isThailand]);

  const featuresDropdownOptions: DropdownValue[] = useMemo(() => {
    return featuresList.reduce((acc: DropdownValue[], feature) => {
      acc.push({
        label: intl.formatMessage({ id: `games.categories.${feature}` }),
        value: feature,
      });
      return acc;
    }, []);
  }, [intl, featuresList]);

  const gameCategories = useMemo(() => {
    let categories: { label: string; value: string }[] = [];

    if (isAuthenticated)
      categories.push({
        label: intl.formatMessage({ id: GIG_GAMES_CATEGORIES_RECORD['last-played'] }),
        value: 'last-played',
      });

    categories.push({
      label: intl.formatMessage({ id: GIG_GAMES_CATEGORIES_RECORD['recommended'] }),
      value: 'recommended',
    });

    categories.push({
      label: intl.formatMessage({ id: GIG_GAMES_CATEGORIES_RECORD['new-games'] }),
      value: 'new-games',
    });

    categories.push({
      label: intl.formatMessage({ id: GIG_GAMES_CATEGORIES_RECORD['live-casino'] }),
      value: 'live-casino',
    });

    categories.push({
      label: intl.formatMessage({ id: GIG_GAMES_CATEGORIES_RECORD['slots'] }),
      value: 'slots',
    });

    categories.push({
      label: intl.formatMessage({ id: GIG_GAMES_CATEGORIES_RECORD['jackpots'] }),
      value: 'jackpots',
    });

    categories.push({
      label: intl.formatMessage({ id: GIG_GAMES_CATEGORIES_RECORD['table-games'] }),
      value: 'table-games',
    });

    categories.push({
      label: intl.formatMessage({ id: GIG_GAMES_CATEGORIES_RECORD['all-games'] }),
      value: 'all-games',
    });

    return categories;
  }, [isAuthenticated]);

  const handleCategoryChange = (category: string) => {
    triggerGTag(GTagEvents.games_nav_category, {
      slug: category,
    });
    navigate('/games/' + category);
    setCategory(category as BffGamesCategory);
  };

  const handleFeatureChange = (feature: string) => {
    triggerGTag(GTagEvents.games_nav_feature, {
      slug: feature,
    });
    if (feature !== detachedDropdownFallbackValue) {
      navigate('/games/' + feature);
      setFeature(feature as BffGamesCategory);
    } else {
      setFeature(undefined);
      navigate('/games');
    }
  };

  const handleProviderChange = (provider: string) => {
    triggerGTag(GTagEvents.games_nav_provider, {
      slug: provider,
    });
    if (provider !== detachedDropdownFallbackValue) {
      navigate(`/games/provider/${provider}`);
      setProvider(provider);
    } else {
      setProvider(undefined);
      navigate('/games');
    }
  };

  // effect which tries to set the initial provider/feature/category if somebody lands on a specific url
  useEffect(() => {
    // to try and find a slug of a category/provider/feature we look for the url segment after the /games/
    const urlChunks = location?.pathname?.split('/');

    // check if games is one of the chunks
    if (!urlChunks || !urlChunks.includes('games')) {
      return;
    }

    // if games is one of the chunks check what is after the games
    const gamesChunkIndex = urlChunks.indexOf('games');

    //check if one of the provider slugs is in the url
    // return if there is nothing after the games
    if (urlChunks.length === gamesChunkIndex) {
      return;
    }

    // if there is something after the games it could be the slug
    const providersSet = new Set(gameProvidersList.map(i => i.slug));
    const providerInUrl = urlChunks.find(item => providersSet.has(item));
    // check if the url slug matches to one of the providers - set the provider as selected
    if (providerInUrl) {
      setProvider(providerInUrl);
    }

    // check if the url slug matches one of the features - set the feature as selected
    const featuresSet = new Set(featuresList);
    const featureInUrl = urlChunks.find(item => featuresSet.has(item as BffGamesCategory));
    if (featureInUrl) {
      setFeature(featureInUrl as BffGamesCategory);
    }

    // check if the url slug matches one of the categories - set the category as selected
    const categoriesSet = new Set(gameCategories.map(i => i.value));
    const categoryInUrl = urlChunks.find(item => categoriesSet.has(item as BffGamesCategory));
    if (categoryInUrl) {
      setCategory(categoryInUrl as BffGamesCategory);
    }

    return () => reset();
  }, [location, gameProvidersList, featuresList]);

  return (
    <div
      className={classNames('sticky z-[90] w-full shadow-md', {
        'top-[55px] md:top-16': !isAuthenticated,
        'top-[-1px] md:top-16': isAuthenticated,
      })}
    >
      <div className="flex overflow-y-visible overflow-x-auto items-center no-scrollbar justify-start lg:justify-center gap-x-2 bg-white py-3 px-4 md:px-8 text-sm">
        <AbsoluteNavLink
          to="/games"
          onClick={() => reset()}
          className={classNames(
            'flex flex-shrink-0 gap-x-2 items-center font-bold rounded-3xl py-2 px-4 [&.active]:bg-blue-blue [&.active]:text-white',
            {
              'bg-blue-blue text-white': location.pathname === '/games',
              'bg-gray-100 text-black': location.pathname !== '/games',
            },
          )}
          end
        >
          <HomeIcon width={15} height={15} />
          {intl.formatMessage({ id: 'games.categories.lobby' })}
        </AbsoluteNavLink>
        {sectionOrder.map(section => {
          if (section === NavSections.Categories) {
            return (
              <CategorySelector
                key="games-nav-categories"
                categories={gameCategories}
                selectedCategory={category}
                onCategoryChange={handleCategoryChange}
                onReset={reset}
              />
            );
          }
          if (section === NavSections.Providers) {
            return (
              <DetachedDropdown
                key="games-nav-providers"
                label={intl.formatMessage({ id: 'games.nav.providers' })}
                options={providersDropdownOptions}
                value={
                  provider
                    ? { value: provider, label: getProviderName(provider) }
                    : initialProviderOption(intl)
                }
                onChange={handleProviderChange}
              />
            );
          }
          if (section === NavSections.Features) {
            return (
              <DetachedDropdown
                key="games-nav-features"
                label={intl.formatMessage({ id: 'games.nav.features' })}
                options={featuresDropdownOptions}
                value={
                  feature
                    ? {
                        value: feature,
                        label: intl.formatMessage({ id: `games.categories.${feature}` }),
                      }
                    : initialFeatureOption(intl)
                }
                onChange={handleFeatureChange}
              />
            );
          }
          return <></>;
        })}
      </div>
    </div>
  );
}

export default GamesCategoryNav;
